import { useCallback, useState } from 'react';
import { tasksApi } from '@/api/tasks';
import { useResource } from '@/hooks/useResource';
import { usePolling } from '@/hooks/usePolling';
import { taskRules } from '@/config/resourceRules';
import type { Task, TaskDetailResponse, TaskFilters } from '@/types/task';

/**
 * Number of tasks listed (the UI does not paginate)
 */
const TASK_LIMIT = 100;

/**
 * How often running tasks are refreshed
 */
export const TASK_POLL_INTERVAL_MS = 2000;

/**
 * Task list + cancel/retry. A retried task runs again in the background:
 * the list is refreshed until it has finished.
 */
export function useTasks(filters: Omit<TaskFilters, 'limit' | 'offset'> = {}) {
  const { type, status, domain_id, certificate_id } = filters;

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await tasksApi.getAll(
        { type, status, domain_id, certificate_id, limit: TASK_LIMIT },
        { signal }
      );
      return response.tasks ?? [];
    },
    [type, status, domain_id, certificate_id]
  );

  const resource = useResource<Task[]>(fetcher, { errorMessage: 'Failed to load tasks' });
  const { reload } = resource;
  const tasks = resource.data ?? [];

  // Tasks retried from this list, followed until they have finished
  const [retriedIds, setRetriedIds] = useState<ReadonlySet<string>>(() => new Set());
  const followsRunningTask = tasks.some((task) => retriedIds.has(task.id) && taskRules.isActive(task));
  usePolling(reload, followsRunningTask ? TASK_POLL_INTERVAL_MS : null);

  const cancelTask = useCallback(
    async (id: string) => {
      await tasksApi.cancel(id);
      await reload();
    },
    [reload]
  );

  const retryTask = useCallback(
    async (id: string) => {
      await tasksApi.retry(id);
      setRetriedIds((prev) => new Set(prev).add(id));
      await reload();
    },
    [reload]
  );

  return {
    tasks,
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    cancelTask,
    retryTask,
  };
}

/**
 * One task with its subtasks (pass null to disable). A pending or running
 * task (e.g. after "Retry Failed") is refreshed until it has finished.
 */
export function useTaskDetail(taskId: string | null) {
  const fetcher = useCallback(
    (signal: AbortSignal) => tasksApi.getById(taskId as string, { signal }),
    [taskId]
  );

  const resource = useResource<TaskDetailResponse>(taskId ? fetcher : null, {
    errorMessage: 'Failed to load task details',
    keepPreviousData: false,
  });

  const task = resource.data ?? null;
  const isActive = taskId !== null && task !== null && taskRules.isActive(task);
  usePolling(resource.reload, isActive ? TASK_POLL_INTERVAL_MS : null);

  return {
    task,
    isLoading: resource.isLoading,
    error: resource.error,
    reload: resource.reload,
  };
}
